import { currencyRateInMxn } from "../../shared/currencyRates";
import Decimal from "decimal.js";

export const emptyHeader = () => ({
  id_empresa: "",
  id_cliente: "",
  id_concepto_presupuesto: "",
  id_precio_especial: "",
  id_almacen: "",
  id_vehiculos: "",
  fecha: new Date().toISOString().slice(0, 10),
  fecha_entrega: "",
  referencia: "",
  orden_compra: "",
  nombre_solicita: "",
  tipo_envio: "",
  id_direccion: "",
  direccion_envio: "",
  horario_entrega: "",
  comentarios: "",
  descuento_pct: 0,
  plazo_dias: 0,
  condiciones_pago: "Pago de contado.",
  fecha_vencimiento: "",
  fecha_revision: "",
  lugar_expedicion: "",
  serie_factura: "",
  forma_pago: "",
  metodo_pago: "",
  uso_comprobante: "",
  razon_social_emisor: "",
  rfc_emisor: "",
  regimen_fiscal_emisor: "",
  razon_social_receptor: "",
  rfc_receptor: "",
  regimen_fiscal_receptor: "",
});

export const extractCollection = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.records)) return response.records;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

export const normalizePage = (response) => {
  const data = extractCollection(response);
  const page =
    response?.data && !Array.isArray(response.data)
      ? response.data
      : response || {};
  const current = Number(page.current_page || page.currentPage || 1);
  const total = Number(page.total || data.length);
  const perPage = Number(page.per_page || page.perPage || 50);
  return {
    data,
    current_page: current,
    last_page: Number(
      page.last_page || page.lastPage || Math.max(1, Math.ceil(total / perPage))
    ),
    total,
  };
};

export const money = (value) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
    Number(value || 0)
  );

export const calculateLine = (line, globalDiscount = 0, discountType = 1) => {
  const quantity = new Decimal(line.cantidad2 || line.cantidad || 0);
  const price = new Decimal(line.precio || 0);
  const linePct = new Decimal(line.descuento_pct || 0);
  const globalPct = new Decimal(globalDiscount || 0);
  const gross = quantity.times(price);
  const requestedDiscount =
    discountType === 0
      ? new Decimal(line.descuento || 0)
      : gross.times(linePct).div(100);
  const lineDiscount = Decimal.min(gross, Decimal.max(0, requestedDiscount));
  const afterLine = gross.minus(lineDiscount);
  const globalAmount = afterLine.times(globalPct).div(100);
  return {
    ...line,
    cantidad: quantity.toNumber(),
    cantidad2: quantity.toNumber(),
    precio: price.toNumber(),
    descuento: lineDiscount.plus(globalAmount).toDecimalPlaces(2).toNumber(),
    descuento_global_pct: globalPct.toNumber(),
    importe: afterLine.minus(globalAmount).toDecimalPlaces(2).toNumber(),
  };
};

export const calculateTotals = (
  lines,
  ivaPct,
  globalDiscount = 0,
  discountType = 1
) => {
  const calculated = lines.map((line) => {
    const result = calculateLine(line, globalDiscount, discountType);
    for (const tax of ["iva", "ieps", "impuestos_adicionales", "retenciones", "retenciones_iva"]) {
      const rate = Number(line[`${tax}_pct`] ?? line.producto?.[tax] ?? (tax === "iva" ? ivaPct : 0));
      result[`${tax}_pct`] = rate;
      result[tax] = new Decimal(result.importe).times(rate).div(100).toDecimalPlaces(2).toNumber();
    }
    return result;
  });
  const subtotal = calculated.reduce(
    (sum, line) => sum.plus(line.importe || 0),
    new Decimal(0)
  );
  const sumTax = (tax) => calculated.reduce((sum, line) => sum.plus(line[tax]), new Decimal(0));
  const iva = sumTax("iva");
  const ieps = sumTax("ieps");
  const additional = sumTax("impuestos_adicionales");
  const retained = sumTax("retenciones");
  const retainedIva = sumTax("retenciones_iva");
  const discount = calculated.reduce(
    (sum, line) => sum.plus(line.descuento || 0),
    new Decimal(0)
  );
  return {
    lines: calculated,
    subtotal: subtotal.toDecimalPlaces(2).toNumber(),
    descuento: discount.toDecimalPlaces(2).toNumber(),
    iva: iva.toDecimalPlaces(2).toNumber(),
    ieps: ieps.toNumber(),
    impuestos_adicionales: additional.toNumber(),
    retenciones: retained.toNumber(),
    retenciones_iva: retainedIva.toNumber(),
    importe: subtotal.plus(iva).plus(ieps).plus(additional).minus(retained).minus(retainedIva).toDecimalPlaces(2).toNumber(),
  };
};

export const buildDetailPayload = ({
  line,
  index,
  ivaPct = 0,
  documentId = null,
  folio = 0,
}) => {
  const cantidad = Number(line.cantidad2 ?? line.cantidad ?? 0);
  const precio = Number(line.precio ?? 0);
  // Sencha envÃ­a el importe bruto de la partida; el descuento viaja separado.
  const importe = new Decimal(cantidad)
    .times(precio)
    .toDecimalPlaces(2)
    .toNumber();
  const descuento = Number(line.descuento ?? 0);
  const porcentajeIva = Number(line.iva_pct ?? ivaPct ?? 0);
  const baseIva = Math.max(0, importe - descuento);

  const detail = {
    folio: Number(line.folio ?? folio ?? 0),
    partida: index + 1,
    cantidad,
    cantidad2: cantidad,
    descripcion: String(line.descripcion || productName(line.producto)).slice(
      0,
      300
    ),
    precio_neto: Number(line.precio_neto ?? precio),
    precio,
    importe,
    descuento_pct: Number(line.descuento_pct ?? 0),
    descuento,
    iva_pct: porcentajeIva,
    iva: Number(
      line.iva ??
        new Decimal(baseIva).times(porcentajeIva).div(100).toDecimalPlaces(2)
    ),
    impuestos_adicionales_pct: Number(line.impuestos_adicionales_pct ?? 0),
    impuestos_adicionales: Number(line.impuestos_adicionales ?? 0),
    retenciones_pct: Number(line.retenciones_pct ?? 0),
    retenciones: Number(line.retenciones ?? 0),
    retenciones_iva_pct: Number(line.retenciones_iva_pct ?? 0),
    retenciones_iva: Number(line.retenciones_iva ?? 0),
    ieps_pct: Number(line.ieps_pct ?? 0),
    ieps: Number(line.ieps ?? 0),
    saldo: Number(line.saldo ?? cantidad),
    costo: Number(line.costo ?? line.producto?.costo_promedio ?? 0),
    costo_promedio: Number(
      line.costo_promedio ?? line.producto?.costo_promedio ?? 0
    ),
    costos_total: Number(line.costos_total ?? 0),
    id_producto: Number(line.id_producto ?? line.producto?.id),
    producto: line,
  };

  if (line.id != null && line.id !== "") detail.id = Number(line.id);
  if (documentId != null && documentId !== "")
    detail.id_encabezado = Number(documentId);
  if (
    line.id_productos_unidad_medida != null &&
    line.id_productos_unidad_medida !== ""
  ) {
    detail.id_productos_unidad_medida = Number(line.id_productos_unidad_medida);
  }
  if (line.id_segmento_negocio != null && line.id_segmento_negocio !== "") {
    detail.id_segmento_negocio = Number(line.id_segmento_negocio);
  }
  if (line.referencia) detail.referencia = String(line.referencia).slice(0, 30);
  if (line.fecha) detail.fecha = String(line.fecha).slice(0, 10);
  if (line.comentarios)
    detail.comentarios = String(line.comentarios).slice(0, 300);
  if (Array.isArray(line.ids_detalles) && line.ids_detalles.length) {
    detail.ids_detalles = line.ids_detalles.map(Number).filter(Number.isFinite);
  }

  return detail;
};

export const productName = (product) =>
  product?.descripcion ||
  product?.nombre ||
  product?.clave ||
  `Producto #${product?.id}`;

export const addressName = (address) =>
  [
    address?.calle,
    address?.num_exterior,
    address?.num_interior ? `Int. ${address.num_interior}` : "",
    address?.colonia,
    address?.municipio,
    address?.estado,
    address?.pais,
  ]
    .filter(Boolean)
    .join(", ");

// Exchange rates express the value of one source currency unit in MXN.
export const convertProductPrice = (price, product, header, currencies) => {
  const sourceId = product.id_moneda_venta || product.moneda_venta?.id;
  if (!sourceId || String(sourceId) === String(header.id_moneda)) return Number(price);
  const mxn = currencies.find((currency) => currency.clave_moneda === "MXN");
  const sourceRate = currencyRateInMxn(sourceId, currencies);
  const targetRate = String(header.id_moneda) === String(mxn?.id) ? 1 : Number(header.tipo_cambio);
  if (!(Number(sourceRate) > 0) || !(targetRate > 0)) throw new Error("Configura el tipo de cambio de la moneda del producto y del documento.");
  return new Decimal(price).times(sourceRate).div(targetRate).toDecimalPlaces(6).toNumber();
};

export function documentProductPrice(product, header, currencies) {
  const presentation = product.presentaciones?.[0] || product.presentaciones_productos?.[0] || {};
  const selected = header.id_precio_especial && (product.precios_especiales || []).find(price =>
    String(price.pivot?.id_precio_especial ?? price.id) === String(header.id_precio_especial));
  const listPrice = selected?.pivot?.monto ?? selected?.monto;
  const originalPrice = listPrice ?? product.precio ?? product.precio_venta ?? presentation.precio ?? 0;
  return convertProductPrice(originalPrice, product, header, currencies);
}
